import React, { useState, useRef } from 'react';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import ListSubheader from '@material-ui/core/ListSubheader';
import TextField from '../text-field';
import ZipInputField, { CanadianZipInputField } from '../zip-input-field';
import colors from '../../themes/main-colors';
import { statesAndProvincesGrouping } from '../utility/state-dropdown';
import { useTranslation } from '../../hooks/useTranslation';
import { MilitaryAddressTooltip } from '../military-address-tooltip';
import { BaseFormModel } from '../../hooks/useFormState.types';

type AlternateAddressProps = {
  form: BaseFormModel;
};

const AlternateAddress = ({ form }: AlternateAddressProps) => {
  const { t } = useTranslation();
  const anchorRef = useRef<any>();
  const [
    showAddressOutsideUSTooltipAnchor,
    setShowAddressOutsideUSTooltipAnchor,
  ] = useState<HTMLElement | null>(null);

  const openAddressOutsideUSTooltip = () => {
    setShowAddressOutsideUSTooltipAnchor(anchorRef.current || null);
  };

  const closeShowAddressOutsideUSTooltip = () => {
    setShowAddressOutsideUSTooltipAnchor(null);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            name={'mailAddress'}
            label={t('STREET_ADDRESS')}
            style={{ width: '100%', marginTop: 20 }}
            inputProps={{
              maxLength: 60,
            }}
            {...form.props('mailAddress')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            name={'mailCity'}
            label={t('CITY')}
            style={{ width: '100%' }}
            inputProps={{
              maxLength: 20,
            }}
            {...form.props('mailCity')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl
            error={Boolean(form.errors.mailState)}
            style={{ width: '100%' }}
            variant="outlined"
          >
            <InputLabel id="state-province-label">
              {t('STATE_PROVINCE_LABEL')}
            </InputLabel>
            <Select
              id="state-province-select"
              labelId="state-province-label"
              name="mailState"
              value={form.values.mailState}
              onChange={async (e: any) => {
                form.onChange(e);
                if (
                  statesAndProvincesGrouping.unitedStates.includes(
                    e.target.value,
                  )
                ) {
                  await Promise.all([
                    form.setValue('mailCountry', 'USA'),
                    form.setValue(
                      'mailZip',
                      form.values.mailZip.length === 5
                        ? form.values.mailZip
                        : '',
                    ),
                  ]);
                } else if (
                  statesAndProvincesGrouping.canada.includes(e.target.value)
                ) {
                  await Promise.all([
                    form.setValue('mailCountry', 'CAN'),
                    form.setValue(
                      'mailZip',
                      form.values.mailZip.length === 7
                        ? form.values.mailZip
                        : '',
                    ),
                  ]);
                } else {
                  await Promise.all([
                    form.setValue('mailCountry', ''),
                    form.setValue('mailZip', ''),
                  ]);
                }
              }}
              error={Boolean(form.errors.mailState)}
              autoWidth
              onBlur={form.onBlur}
              style={{ width: '100%' }}
              label={t('STATE_PROVINCE_LABEL')}
              variant="outlined"
              native
            >
              <option aria-label="None" value="" />
              <optgroup
                style={{ backgroundColor: '#fff' }}
                label={t('UNITED_STATES')}
              >
                {statesAndProvincesGrouping.unitedStates.map(stateValue => (
                  <option key={stateValue} value={stateValue}>
                    {stateValue}
                  </option>
                ))}
              </optgroup>
              <optgroup style={{ backgroundColor: '#fff' }} label={t('CANADA')}>
                {statesAndProvincesGrouping.canada.map(stateValue => (
                  <option key={stateValue} value={stateValue}>
                    {stateValue}
                  </option>
                ))}
              </optgroup>
            </Select>
            <FormHelperText>{form.errors.mailState}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          {statesAndProvincesGrouping.canada.includes(form.values.mailState) ? (
            <CanadianZipInputField
              name={'mailZip'}
              label={t('ZIP_POSTAL_CODE')}
              style={{ width: '100%' }}
              inputProps={{ minLength: 1, maxLength: 7 }}
              {...form.props('mailZip')}
            />
          ) : (
            <ZipInputField
              name={'mailZip'}
              label={t('ZIP_POSTAL_CODE')}
              style={{ width: '100%' }}
              inputProps={{ minLength: 1, maxLength: 5 }}
              {...form.props('mailZip')}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name={'inCareOf'}
            label={t('IN_CARE_OF')}
            style={{ width: '100%' }}
            {...form.props('inCareOf')}
          />
        </Grid>
        <Grid item xs={12}>
          <a
            ref={anchorRef}
            style={{
              cursor: 'pointer',
              display: 'inline',
              color: colors.sparkBlue,
              borderBottom: `2px dotted ${colors.sparkBlue}`,
              textDecoration: 'none',
              fontSize: '1.0rem',
            }}
            onClick={(e: any) => {
              e.preventDefault();
              openAddressOutsideUSTooltip();
            }}
            href="#"
          >
            {t('MAILING_ADDRESS_OUTSIDE_US')}
          </a>
          <MilitaryAddressTooltip
            isOpen={Boolean(showAddressOutsideUSTooltipAnchor)}
            closeTooltip={closeShowAddressOutsideUSTooltip}
            anchorEl={showAddressOutsideUSTooltipAnchor}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AlternateAddress;
